import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  getUnixTime,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
  subWeeks,
  subYears,
} from 'date-fns';
import { DashboardLayoutObject } from 'src/models/dashboard/_types';
import { hasFeatureFlag } from 'src/shared/usePermissionCheck';

import { FeatureFlag, PlatformTab } from './_types';
import { Platform } from './_types';

import type { Session } from '../session/_types';

export const supportsModalAlerts = (session: Session): boolean => {
  return (
    (session.managedAppsNumber !== undefined &&
      session.currentIosVersion >= '5.9.0') ||
    hasFeatureFlag(session, FeatureFlag.EnableNewAppFeatures)
  );
};

type GetDatesFromRangeTypeOptions = {
  compareToRange?: string | { start: Date; end: Date };
  doNotLimitEndDate?: boolean;
  baseDate?: Date;
};

/**
 * Checks if logging is disabled for the session
 * @param session - The session to check
 * @returns True if logging is disabled, false otherwise
 */
export const hasLoggingDisabled = (session?: Session) => {
  if (!session) return false;

  return (
    session.disableLoggingCustomWorkouts &&
    session.disableLoggingFromFavoriteWorkouts &&
    session.disableLoggingFromScheduledWorkouts
  );
};

export const getDatesFromRangeType = (
  rangeType: string,
  options?: GetDatesFromRangeTypeOptions
): {
  startDate: Date;
  endDate: Date;
} | null => {
  const { doNotLimitEndDate, compareToRange } = options || {};
  const now = new Date();
  let startDate: Date | null = null;
  let endDate: Date | null = null;
  let currentStartDate: Date = now;
  let currentEndDate: Date = now;

  if (compareToRange && typeof compareToRange === 'string') {
    const compareToRangeDates = getDatesFromRangeType(compareToRange, {
      doNotLimitEndDate,
    });
    if (compareToRangeDates) {
      currentStartDate = compareToRangeDates.startDate;
      currentEndDate = compareToRangeDates.endDate;
    }
  }

  if (
    compareToRange &&
    typeof compareToRange !== 'string' &&
    compareToRange?.start
  ) {
    currentStartDate = compareToRange.start;
    currentEndDate = compareToRange.end;
  }

  // Helper to limit end date to today unless it's a future range
  const limitEndDate = (date: Date): Date => {
    if (doNotLimitEndDate) return date;

    return date > now ? endOfDay(now) : date;
  };

  if (rangeType === 'allTime') {
    startDate = startOfDay(subYears(now, 10));
    endDate = limitEndDate(endOfDay(addYears(now, 10)));
  } else if (rangeType === 'today') {
    startDate = startOfDay(now);
    endDate = limitEndDate(endOfDay(now));
  } else if (rangeType === 'yesterday') {
    const yesterday = subDays(now, 1);
    startDate = startOfDay(yesterday);
    endDate = limitEndDate(endOfDay(yesterday));
  } else if (rangeType === 'lastMonth') {
    startDate = startOfMonth(subMonths(now, 1));
    endDate = limitEndDate(endOfMonth(subMonths(now, 1)));
  } else if (rangeType === 'thisMonth') {
    startDate = startOfMonth(now);
    endDate = limitEndDate(endOfMonth(now));
  } else if (rangeType === 'month') {
    startDate = startOfDay(subMonths(now, 1));
    endDate = limitEndDate(endOfDay(now));
  } else if (rangeType === 'week') {
    startDate = startOfDay(subDays(now, 7));
    endDate = limitEndDate(endOfDay(now));
  } else if (rangeType === 'lastWeek') {
    const lastWeek = subDays(now, 7);
    startDate = startOfWeek(lastWeek, { weekStartsOn: 0 });
    endDate = limitEndDate(endOfWeek(lastWeek, { weekStartsOn: 0 }));
  } else if (rangeType === 'thisWeek') {
    startDate = startOfWeek(now, { weekStartsOn: 0 });
    endDate = limitEndDate(endOfWeek(now, { weekStartsOn: 0 }));
  } else if (rangeType === 'lastYear') {
    startDate = startOfYear(subYears(now, 1));
    endDate = limitEndDate(endOfYear(subYears(now, 1)));
  } else if (rangeType === 'thisYear') {
    startDate = startOfYear(now);
    endDate = limitEndDate(endOfYear(now));
  } else if (rangeType === 'thisQuarter') {
    startDate = startOfQuarter(now);
    endDate = limitEndDate(endOfQuarter(now));
  } else if (rangeType === 'lastQuarter') {
    startDate = startOfQuarter(subQuarters(now, 1));
    endDate = limitEndDate(endOfQuarter(subQuarters(now, 1)));
  } else if (rangeType === 'next30Days') {
    startDate = startOfDay(now);
    endDate = endOfDay(addDays(now, 30));
  } else if (rangeType === 'next7Days') {
    startDate = startOfDay(now);
    endDate = endOfDay(addDays(now, 7));
  } else if (rangeType === 'nextWeek') {
    startDate = startOfWeek(addWeeks(now, 1), { weekStartsOn: 0 });
    endDate = endOfWeek(addWeeks(now, 1), { weekStartsOn: 0 });
  } else if (rangeType === 'nextMonth') {
    startDate = startOfMonth(addMonths(now, 1));
    endDate = endOfMonth(addMonths(now, 1));
  } else if (rangeType === 'current') {
    startDate = null;
    endDate = null;
  } else if (/(\d+)Month[s]?Ago/.test(rangeType)) {
    if (currentStartDate && currentEndDate) {
      const monthsAgo = parseInt(
        rangeType.match(/(\d+)Month[s]?Ago/)?.[1] || '1'
      );
      startDate = subMonths(currentStartDate, monthsAgo);
      endDate = limitEndDate(endOfMonth(subMonths(currentEndDate, monthsAgo)));
    }
  } else if (/(\d+)Week[s]?Ago/.test(rangeType)) {
    if (currentStartDate && currentEndDate) {
      const weeksAgo = parseInt(
        rangeType.match(/(\d+)Week[s]?Ago/)?.[1] || '1'
      );
      startDate = subWeeks(currentStartDate, weeksAgo);
      endDate = limitEndDate(endOfWeek(subWeeks(currentEndDate, weeksAgo)));
    }
  } else if (/(\d+)Year[s]?Ago/.test(rangeType)) {
    if (currentStartDate && currentEndDate) {
      const yearsAgo = parseInt(
        rangeType.match(/(\d+)Year[s]?Ago/)?.[1] || '1'
      );
      startDate = subYears(currentStartDate, yearsAgo);
      endDate = limitEndDate(endOfYear(subYears(currentEndDate, yearsAgo)));
    }
  } else if (/(\d+)Quarter[s]?Ago/.test(rangeType)) {
    if (currentStartDate && currentEndDate) {
      const quartersAgo = parseInt(
        rangeType.match(/(\d+)Quarter[s]?Ago/)?.[1] || '1'
      );
      startDate = subQuarters(currentStartDate, quartersAgo);
      endDate = limitEndDate(
        endOfQuarter(subQuarters(currentEndDate, quartersAgo))
      );
    }
  }

  return startDate && endDate ? { startDate, endDate } : null;
};

export const supportsV2Navigation = (platform: Platform): boolean => {
  return (
    platform.featureFlags?.includes(FeatureFlag.EnableNewAppFeatures) ||
    platform.currentIosVersion >= '5.11.0'
  );
};

export const generatePredefinedTabs = (platform: Platform) => {
  const host = hostOrSubdomain(platform);

  const predefinedTabs = [
    {
      name: 'Dashboard',
      icon: 'fa-home',
      url: `wtcom://dashboard`,
      description:
        'Your app dashboard. Includes scheduled workouts, checkins, etc.',
    },
    {
      name: 'Appointments',
      icon: 'fa-calendar',
      url: `https://${host}/ex4/appointments/`,
      description:
        'Allow you and your clients to view and manage booked sessions.',
    },
    {
      name: 'Booking',
      icon: 'fa-calendar-plus',
      url: `https://${host}/a/booking/`,
      description: 'Allow your clients to book new sessions.',
    },
    {
      name: 'Staff Only Schedule',
      icon: 'fa-calendar-alt',
      url: `https://${host}/ex4/calendar/`,
      visibility: ['trainer'],
      description: 'Allow your staff to view and manage their calendar.',
    },
    {
      name: 'Manage Accounts',
      icon: 'fa-users',
      url: `https://${host}/ex4/clients/`,
      visibility: ['trainer'],
      description: 'Allow your staff to view and manage their clients.',
    },
    {
      name: 'My Packages',
      icon: 'fa-box',
      url: `https://${host}/ex4/my-packages/`,
      description: 'Allow your clients to view and manage their packages.',
    },
    {
      name: 'Packages For Sale',
      icon: 'fa-store',
      url: `https://${host}/a/packages/`,
      description: 'Allow your clients to purchase packages.',
    },
    {
      name: 'Plans',
      icon: 'fa-clipboard-list',
      url: `https://${host}/a/app-webviews/workout-plans/`,
      description: 'Allow your clients to follow plans and group calendars.',
    },
    {
      name: 'Resources',
      icon: 'fa-file-alt',
      url: `https://${host}/ex4/mobile/content/?type=resource`,
      description: 'Allow your clients to view their resources.',
    },
    {
      name: 'Videos',
      icon: 'fa-video',
      url: `https://${host}/ex4/mobile/content/?type=video`,
      description: 'Allow your clients to view their videos.',
    },
    {
      name: 'Combined Resources and Videos',
      icon: 'fa-file-alt',
      url: `https://${host}/ex4/mobile/content/`,
      description: 'Allow your clients to view their resources and videos.',
    },
    {
      name: 'Messages',
      icon: 'fa-comments',
      url: `https://${host}/ex4/messages/`,
      description: 'Allow your clients to send messages to staff.',
    },
    {
      name: 'Billing Info',
      icon: 'fa-credit-card',
      url: `https://${host}/ex4/me/account/`,
      description: 'Allow your clients to view their billing information.',
    },
    {
      name: 'Groups',
      icon: 'fa-users',
      url: `wtcom://groups`,
      description: 'Allow your clients to view their groups.',
    },
    {
      name: 'Exercise Library',
      icon: 'fa-dumbbell',
      url: `wtcom://exercises`,
      description: 'Allow your clients to view your exercise library.',
    },
    {
      name: 'Log Measurements',
      icon: 'fa-ruler',
      url: `https://${host}/ex4/me/measurements/`,
      description: 'Allow your clients to log measurements.',
    },
    {
      name: 'Measurements',
      icon: 'fa-weight',
      url: `https://${host}/ex4/me/health/`,
      description:
        'Allow your clients to view their measurement history in a graph.',
    },
    {
      name: 'Performance',
      icon: 'fa-chart-line',
      url: `https://${host}/ex4/me/performance/`,
      description:
        'Allow your clients to view workout and exercise performance.',
    },
    {
      name: 'Assessments',
      icon: 'fa-clipboard-check',
      url: `https://${host}/ex4/me/assessments/`,
      description: 'Allow your clients to view and complete assessments.',
    },
    {
      name: 'Nutrition',
      icon: 'fa-apple-alt',
      url: `https://${host}/ex4/me/nutrition/`,
      description:
        'Allow your clients to view their nutrition notes and macros.',
    },
    {
      name: 'My Connected Apps',
      icon: 'fa-plug',
      url: `https://${host}/ex4/me/apps/`,
      description: 'Allow your clients to connect to 3rd party apps.',
    },
    {
      name: 'Wearables Connected Apps',
      icon: 'fa-watch',
      url: `https://${host}/ex4/connections/`,
      description: 'Allow your clients to connect to wearables.',
    },
    {
      name: 'My Rep Maxes',
      icon: 'fa-trophy',
      url: `https://${host}/ex4/me/rep-maxes/`,
      description: 'Allow your clients to view their rep maxes.',
    },
    {
      name: 'Leaderboards',
      icon: 'fa-list-ol',
      url: `https://${host}/ex4/leaderboards/public/`,
      description: 'Allow your clients to view leaderboards.',
    },
    {
      name: 'Progress Photos',
      icon: 'fa-images',
      url: `https://${host}/ex4/me/progress-photos/`,
      description: 'Allow your clients to view and add progress photos.',
    },
    {
      name: 'Log Workout',
      icon: 'fa-running',
      url: `wtcom://workout-selector`,
      description: 'Clients can log a new workout.',
    },
    {
      name: 'Activity',
      icon: 'fa-users',
      url: `https://${host}/a/app-webviews/activity/`,
      description: 'Allow your clients to view activity from other accounts.',
    },
    {
      name: 'Settings',
      icon: 'fa-cog',
      url: `wtcom://settings`,
      description: 'Allow your clients to manage their profile settings.',
    },
    {
      name: 'In-App Purchases',
      icon: 'fa-store',
      url: `wtcom://store`,
      description: 'Allow your clients to make in-app purchases on iOS.',
    },
    {
      name: 'Door Unlock (Legacy)',
      icon: 'fa-lock',
      url: 'https://${host}/a/app-webviews/fbm/unlock/',
      description: 'Allow your clients to unlock a door managed by Kisi.',
    },
    {
      name: 'Door Unlock',
      icon: 'fa-lock',
      url: `wtcom://unlock-door`,
      description: 'Allow your clients to unlock a door managed by Kisi.',
    },
    {
      name: 'Snapshot',
      icon: 'fa-camera',
      url: `https://${host}/ex4/me/snapshots/`,
      description:
        'Allow your clients to view a snapshot of their workout history.',
    },
    {
      name: 'Products',
      icon: 'fa-box',
      url: `https://${host}/ex4/purchase/products/`,
      description: 'Allow your clients to purchase products.',
    },
    {
      name: 'Change PIN',
      icon: 'fa-lock',
      url: `https://${host}/ex4/me/pin/`,
      description: 'Allow your clients/staff to change their Kiosk/POS PIN.',
    },
    {
      name: 'Refer a Friend',
      icon: 'fa-user-plus',
      url: `https://${host}/ex4/invite/`,
      description: 'Allow your clients to refer a friend via purchase URL.',
    },
    {
      name: 'Dependents',
      icon: 'fa-users',
      url: `https://${host}/ex4/me/dependents/`,
      description: 'Allow your clients to view their dependents in a table view.',
    },
    {
      name: 'Visits',
      icon: 'fa-calendar-alt',
      url: `https://${host}/ex4/me/visits/`,
      description: 'Allow your clients to view their visits in a table view.',
    },
    {
      name: 'Group Calendars',
      icon: 'fa-calendar-alt',
      url: `https://${host}/ex4/mobile/group-calendars/`,
      description: 'Allow your clients to follow and unfollow from a list of group calendars.',
    }
  ];

  return predefinedTabs;
};

export const humanizeRangeType = (rangeType: string): string => {
  return rangeType
    .replace(/(\d+)([A-Z][a-z]*)/g, '$1 $2') // Handle numbers followed by words
    .replace(/([a-z])(\d+)/g, '$1 $2') // Handle words followed by numbers
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
    .trim(); // Remove any extra spaces
};

export const getReportUrl = (
  kpi: DashboardLayoutObject,
  startDate?: Date | null,
  endDate?: Date | null
): string | null => {
  let link = `${window.location.protocol}//${window.location.host}/ex4/report/`;
  if (window.location.port === '3000') link = link.replace('3000', '5001');

  const params: Record<string, string> = {
    f_refresh: 'true',
  };

  if (startDate) {
    params.f_startDate = new Date(startDate).toISOString();
  }
  if (endDate) {
    params.f_endDate = new Date(endDate).toISOString();
  }

  let type = '';
  switch (kpi.originalId || kpi.id) {
    case 'gross_sales':
      type = 'itemized_sales';
      break;
    case 'product_sales':
      type = 'itemized_sales';
      params.f_itemType = 'Exercisecom::Product';
      break;
    case 'package_sales':
      type = 'itemized_sales';
      params.f_itemType = 'Fbm::Package';
      break;
    case 'group_sales':
      type = 'itemized_sales';
      params.f_itemType = 'Group';
      break;
    case 'plan_sales':
      type = 'itemized_sales';
      params.f_itemType = 'WorkoutPlan';
      break;
    case 'succeeded_charges':
    case 'refunded_charges':
    case 'failed_charges':
      type = 'charges';
      break;
    case 'active_clients':
    case 'new_clients':
      type = 'client_filters';
      break;
    case 'booked_visits':
    case 'scheduled_visits':
      type = 'detailed_visits';
      break;
    case 'projected_sales':
      type = 'projected_revenue';
      break;
    case 'complete_visits':
      type = 'detailed_visits';
      params.f_visitStatus = 'completed';
      break;
    case 'cancel_visits':
      type = 'detailed_visits';
      params.f_visitStatus = 'cancelled';
      break;
    case 'noshow_visits':
      type = 'detailed_visits';
      params.f_visitStatus = 'noshowed';
      break;
    case 'visit_count':
      type = 'detailed_visits';
      break;
    case 'active_package_subscription':
    case 'clients_on_active_package_subscription':
      type = 'active_subscription';
      params.f_startDate = params.f_endDate;
      params.f_itemType = 'Fbm::Package';
      break;
    case 'clients_on_active_one_time_free_package':
      type = 'client_packages';
      params.f_startDate = params.f_endDate;
      params.f_packagePaymentTypes = 'free';
      break;
    case 'clients_on_active_one_time_package':
      type = 'client_packages';
      params.f_startDate = params.f_endDate;
      params.f_packagePaymentTypes = 'single_purchase';
      break;
    case 'active_packages':
    case 'active_package_users':
    case 'total_package_users':
    case 'total_packages':
      type = 'client_packages';
      params.f_startDate = params.f_endDate;
      break;
    case 'active_subscriptions':
      type = 'active_subscription';
      params.f_startDate = params.f_endDate;
      break;
    case 'paused':
      type = 'paused_subscriptions';
      delete params.f_startDate;
      delete params.f_endDate;
      break;
    case 'scheduled_subscriptions':
      type = 'active_subscription';
      params.f_subscriptionType = 'scheduled';
      delete params.f_startDate;
      delete params.f_endDate;
      break;
    case 'pending_cancel':
      type = 'active_subscription';
      params.f_subscriptionType = 'pending_cancel';
      delete params.f_startDate;
      delete params.f_endDate;
      break;
    case 'lead_assessment_completed':
      type = 'user_assessments';
      params.f_leadAssessment = 'true';
      break;
    case 'subscription_sales':
      type = 'itemized_sales';
      params.f_purchaseType = 'subscription';
      delete params.f_startDate;
      delete params.f_endDate;
      break;
    case 'cancelled_subscriptions':
      type = 'cancelled_subscriptions';
      break;
    case 'new_subscriptions':
      type = 'started_subscription';
      break;
    case 'total_workouts':
      type = 'daily_logged_workouts';
      break;
    case 'total_plan_followers':
      type = 'daily_plan_followers';
      break;
    case 'lifecycle_stage_count':
      if (kpi.rangeType === 'current') {
        type = 'client_filters';
        delete params.f_startDate;
        delete params.f_endDate;
      }
      break;
    case 'new_accounts':
      type = 'client_filters';
      params.f_customStatus = JSON.stringify({
        value: 'new_clients',
        start: startDate ? getUnixTime(startDate) : null,
        end: endDate ? getUnixTime(endDate) : null,
      });
      break;
    case 'failed_payments':
      type = 'client_filters';
      params.f_customStatus = JSON.stringify({
        value: 'failed_payments',
        start: startDate ? getUnixTime(startDate) : null,
        end: endDate ? getUnixTime(endDate) : null,
      });
      break;
    case 'no_logged_workouts':
      type = 'client_filters';
      params.f_customStatus = JSON.stringify({
        value: 'not_logged_workout',
        start: startDate ? getUnixTime(startDate) : null,
        end: endDate ? getUnixTime(endDate) : null,
      });
      break;
    case 'expiring_package_accounts':
      type = 'client_filters';
      params.f_customStatus = JSON.stringify({
        value: 'expiring_package',
        start: startDate ? getUnixTime(startDate) : null,
        end: endDate ? getUnixTime(endDate) : null,
      });
      break;
    case 'expiring_card_accounts':
      type = 'client_filters';
      params.f_customStatus = JSON.stringify({
        value: 'expiring_card',
        start: startDate ? getUnixTime(startDate) : null,
        end: endDate ? getUnixTime(endDate) : null,
      });
      break;
    case 'completed_assessment':
      type = 'user_assessments';
      break;
    case 'birthday_soon':
      type = 'client_filters';
      params.f_customStatus = JSON.stringify({
        value: 'birthday_today',
        start: startDate ? getUnixTime(startDate) : null,
        end: endDate ? getUnixTime(endDate) : null,
      });
      break;
    case 'no_recent_visits':
      type = 'client_filters';
      params.f_customStatus = JSON.stringify({
        value: 'no_visit',
        start: startDate ? getUnixTime(startDate) : null,
        end: endDate ? getUnixTime(endDate) : null,
      });
      break;
    case 'calendar_plan_ending':
      type = 'client_filters';
      params.f_customStatus = JSON.stringify({
        value: 'calendar_plan_ending',
        start: startDate ? getUnixTime(startDate) : null,
        end: endDate ? getUnixTime(endDate) : null,
      });
      break;
    case 'calendar_ending':
      type = 'client_filters';
      params.f_customStatus = JSON.stringify({
        value: 'calendar_ending',
        start: startDate ? getUnixTime(startDate) : null,
        end: endDate ? getUnixTime(endDate) : null,
      });
      break;
    case 'new_uploads':
      type = 'client_filters';
      params.f_customStatus = JSON.stringify({
        value: 'new_uploads',
        start: startDate ? getUnixTime(startDate) : null,
        end: endDate ? getUnixTime(endDate) : null,
      });
      break;
    case 'new_messages':
      type = 'client_filters';
      params.f_customStatus = JSON.stringify({
        value: 'new_messages',
        start: startDate ? getUnixTime(startDate) : null,
        end: endDate ? getUnixTime(endDate) : null,
      });
      break;
    case 'no_package_accounts':
      type = 'client_filters';
      params.f_customStatus = JSON.stringify({
        value: 'owned_package',
        number: -1,
        start: startDate ? getUnixTime(startDate) : null,
        end: endDate ? getUnixTime(endDate) : null,
      });
      break;
    default:
      return null;
  }

  if (!type) return '';

  if (params.f_startDate && params.f_endDate) {
    params.f_date = JSON.stringify({
      startDate: params.f_startDate,
      endDate: params.f_endDate,
      date: params.f_endDate,
      dateFilter: '',
      utc: true,
    });
    delete params.f_startDate;
    delete params.f_endDate;
  }

  if (kpi.serviceIds) {
    params.f_serviceId = kpi.serviceIds.join(',');
  }

  if (kpi.packageTags) {
    params.f_packageTag = kpi.packageTags.join(',');
  }

  if (kpi.planIds) {
    params.f_workoutPlanId = kpi.planIds.join(',');
    params.f_calendarId = kpi.planIds.join(',');
  }

  if (kpi.visitStatuses) {
    params.f_visitStatus = kpi.visitStatuses.join(',');
  }

  if (kpi.lifecycleStageIds) {
    params.f_lifecycleStage = kpi.lifecycleStageIds.join(',');
  }

  if (kpi.subscriptionTypes) {
    params.f_subscriptionType = kpi.subscriptionTypes.join(',');
  }

  if (kpi.siteIds) {
    params.f_sites = kpi.siteIds.join(',');
  }

  link += type + '?';

  const queryString = Object.entries(params)
    .filter(([, value]) => value !== null)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');

  return `${link}${queryString}`;
};

export const appVersionAtLeast = (
  session: Session,
  version: string,
  currentVersion = ''
) => {
  const currentVersionParts = (
    currentVersion ||
    session.currentIosVersion ||
    '1.0.0'
  )
    .split('.')
    .map((part) => parseInt(part));
  const versionParts = version.split('.').map((part) => parseInt(part));

  return (
    currentVersionParts[0] >= versionParts[0] &&
    currentVersionParts[1] >= versionParts[1] &&
    currentVersionParts[2] >= versionParts[2]
  );
};

export const supportsNewAppPlansPage = (session: Session) => {
  return (
    appVersionAtLeast(session, '5.11.0') ||
    hasFeatureFlag(session, FeatureFlag.EnableNewAppFeatures)
  );
};

/**
 * Combines clientDashboard and mainNav items from platformTabs,
 * preventing duplicates if they appear to be already merged.
 */
export const getCombinedClientNavItems = (
  platformTabs: Platform['platformTabs'] | null | undefined
): PlatformTab[] => {
  const clientDashboard = platformTabs?.clientDashboard || [];
  // Filter out the default 'Dashboard' item from mainNav as before
  const mainNav = (platformTabs?.mainNav || []).filter(
    (item) => item.id !== 'Dashboard'
  );

  if (!mainNav.length) {
    return [...clientDashboard]; // Return a copy
  }

  const mainNavIds = new Set(mainNav.map((item) => item.id));
  const alreadyMerged = clientDashboard.some((item) => mainNavIds.has(item.id));

  return alreadyMerged
    ? [...clientDashboard]
    : [...clientDashboard, ...mainNav];
};

// Validation for app menus and navigation
export const APP_VERSION_REGEX = /^\d+\.\d+\.\d+$/;
export const URL_REGEX = /^(wtcom:\/\/|https:\/\/).+$/;
export const DEVELOPMENT_URL_REGEX = /^(wtcom:\/\/|https?:\/\/).+$/;

/**
 * Creates a wrapper for the validation functions that creates proper Yup ValidationError objects
 * @param validateFn The validation function to wrap
 */
export const createYupValidator = (
  validateFn: (value?: string) => true | { message: string; path: string }
) => {
  // Return a function that matches Yup's TestFunction signature
  return function (this: any, value?: string) {
    // Simply use the validation function without user/platform context for now
    const result = validateFn(value);
    if (result === true) return true;

    // Create a Yup ValidationError
    return this.createError({
      path: this.path,
      message: result.message,
    });
  };
};

/**
 * Validates app URLs according to platform rules:
 * - No spaces allowed
 * - Must start with wtcom:// or https://
 * - wtcom:// URLs must not end with /
 * - https:// URLs must end with / (before ? or #)
 *
 * @param url URL to validate
 * @returns ValidationError if invalid, true if valid
 */
export const validateAppUrl = (url?: string) => {
  // Check for spaces
  if (!url) {
    return {
      message: 'App URL is required',
      path: 'url',
    };
  }

  // Check for spaces
  if (url.includes(' ')) {
    return {
      message: 'App URL cannot contain a space',
      path: 'url',
    };
  }

  // Must start with wtcom:// or https://
  if (!url.startsWith('wtcom://') && !url.startsWith('https://') && import.meta.env.MODE !== 'development') {
    return {
      message: 'App URL must start with "wtcom://" or "https://"',
      path: 'url',
    };
  }

  // For wtcom:// URLs
  if (url.startsWith('wtcom://')) {
    if (url.endsWith('/')) {
      return {
        message:
          'App URL that link directly to an in-app page cannot end in a backslash',
        path: 'url',
      };
    }
  } else {
    // Must end with a slash, but the placement depends on ? and #
    if (url.includes('?')) {
      if (!url.includes('/?')) {
        return {
          message: 'App URL must have a backslash before the "?"',
          path: 'url',
        };
      }
    } else if (url.includes('#')) {
      if (!url.includes('/#')) {
        return {
          message: 'App URL must have a backslash before the "#"',
          path: 'url',
        };
      }
    } else if (!url.endsWith('/')) {
      return {
        message: 'App URL must end in a backslash',
        path: 'url',
      };
    }

    // Check if URL is valid using regex
    const urlTest = import.meta.env.MODE === 'development' ? !DEVELOPMENT_URL_REGEX.test(url) : !URL_REGEX.test(url)
    if (urlTest) {
      return {
        message: 'This is not a valid URL',
        path: 'url',
      };
    }

    // Skip platform host validation to simplify
    // We'd need more complex context passing for this
  }

  return true;
};

/**
 * Validates app version strings to ensure they are in format X.X.X
 *
 * @param version Version string to validate
 * @returns ValidationError if invalid, true if valid
 */
export const validateAppVersion = (version?: string) => {
  if (!version) return true;

  if (!APP_VERSION_REGEX.test(version)) {
    return {
      message: 'Must be in the format X.X.X',
      path: 'version',
    };
  }

  return true;
};

export const hostOrSubdomain = (platform?: Platform) => {
  if (!platform || (!platform.host && !platform.subdomain)) return 'exercise.com';

  return platform.host ? platform.host : `${platform.subdomain}.exercise.com`;
};

/**
 * Yup-compatible validator for app URLs
 */
export const yupValidateAppUrl = createYupValidator(validateAppUrl);

/**
 * Yup-compatible validator for app versions
 */
export const yupValidateAppVersion = createYupValidator(validateAppVersion);

export const isHQPlatform = (platform: Platform | Session | undefined) => {
  return platform?.platformType === 'hq';
};

export const platformLabel = (platform: Platform | Session | undefined) => {
  return platform?.brand || platform?.trainerName || platform?.subdomain;
};

export const isStripeConnected = (platform: Platform | Session | undefined): boolean => {
  return !!platform?.stripeAccountId;
};
